import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCurrentProfile,
  updateProfile,
} from "../../../api/services/profile";
import type {
  UpdateProfileRequest,
  Profile,
} from "../../../api/services/profile/types";
import {
  createPaymentCard,
  getPaymentCards,
  getPaymentCardById,
  updatePaymentCard,
  deletePaymentCard,
  getUserBalance,
  addBalance,
} from "../../../api/services/payment";
import type {
  CreateCardRequest,
  UpdateCardRequest,
  AddBalanceRequest,
} from "../../../api/services/payment/types";
import {
  getUserOrders,
  getUserOrderById,
  getOrderPayment,
  createOrderSession,
  getOrderSession,
  setUserInfo,
  bookSeats,
  processPayment,
  claimGuestSession,
  applyCoupon,
} from "../../../api/services/orders";
import type {
  CreateSessionRequest,
  SetUserInfoRequest,
  BookSeatsRequest,
  ProcessPaymentRequest,
  ClaimGuestSessionRequest,
  ApplyCouponRequest,
} from "../../../api/services/orders/types";

/**
 * Hook to fetch the current user's profile
 * Note: This is also used in AuthContext, so invalidating ['profile'] will refetch in both places
 */
export const useGetProfile = () => {
  return useQuery({
    queryKey: ["profile"],
    queryFn: getCurrentProfile,
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to update the current user's profile
 * Automatically invalidates the profile cache on success
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => updateProfile(data),
    onSuccess: (updatedProfile: Profile) => {
      // Update the cache with the new profile data
      queryClient.setQueryData(["profile"], updatedProfile);

      // Optionally invalidate to ensure fresh data from server
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
};

// ============================================
// Payment Cards Hooks
// ============================================

/**
 * Hook to fetch all payment cards for the current user
 */
export const useGetPaymentCards = () => {
  return useQuery({
    queryKey: ["payment-cards"],
    queryFn: getPaymentCards,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch a specific payment card by ID
 */
export const useGetPaymentCard = (cardId: string) => {
  return useQuery({
    queryKey: ["payment-card", cardId],
    queryFn: () => getPaymentCardById(cardId),
    enabled: !!cardId,
  });
};

/**
 * Hook to create a new payment card
 * Invalidates the payment cards list on success
 */
export const useCreatePaymentCard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCardRequest) => createPaymentCard(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payment-cards"] });
    },
  });
};

/**
 * Hook to update a payment card
 * Invalidates both the specific card and the cards list on success
 */
export const useUpdatePaymentCard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cardId,
      data,
    }: {
      cardId: string;
      data: UpdateCardRequest;
    }) => updatePaymentCard(cardId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["payment-card", variables.cardId],
      });
      queryClient.invalidateQueries({ queryKey: ["payment-cards"] });
    },
  });
};

/**
 * Hook to delete a payment card
 * Invalidates the payment cards list on success
 */
export const useDeletePaymentCard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cardId: string) => deletePaymentCard(cardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payment-cards"] });
    },
  });
};

/**
 * Hook to fetch the user's balance
 */
export const useGetUserBalance = () => {
  return useQuery({
    queryKey: ["user-balance"],
    queryFn: getUserBalance,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

// ============================================
// Orders Hooks
// ============================================

/**
 * Hook to fetch all user orders
 */
export const useGetUserOrders = () => {
  return useQuery({
    queryKey: ["user-orders"],
    queryFn: async () => {
      const response = await getUserOrders();
      return response.orders;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch a specific order by ID
 */
export const useGetUserOrderById = (orderId?: string) => {
  return useQuery({
    queryKey: ["user-order", orderId],
    queryFn: async () => {
      if (!orderId) return null;
      const response = await getUserOrderById(orderId);
      return response.order;
    },
    enabled: !!orderId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to fetch order payment details
 */
export const useGetOrderPayment = (orderId?: string) => {
  return useQuery({
    queryKey: ["order-payment", orderId],
    queryFn: async () => {
      if (!orderId) return null;
      const response = await getOrderPayment(orderId);
      return response.payment;
    },
    enabled: !!orderId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Hook to create an order session
 */
export const useCreateOrderSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSessionRequest) => createOrderSession(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order-sessions"] });
    },
  });
};

/**
 * Hook to fetch a specific order session
 */
export const useGetOrderSession = (sessionId: string) => {
  return useQuery({
    queryKey: ["order-session", sessionId],
    queryFn: () => getOrderSession(sessionId),
    enabled: !!sessionId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to set user info for an order session
 */
export const useSetUserInfo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SetUserInfoRequest) => setUserInfo(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["order-session", variables.sessionId],
      });
    },
  });
};

/**
 * Hook to book seats for an order
 */
export const useBookSeats = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BookSeatsRequest) => bookSeats(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["order-session", variables.sessionId],
      });
    },
  });
};

/**
 * Hook to process payment for an order
 */
export const useProcessPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProcessPaymentRequest) => processPayment(data),
    onSuccess: () => {
      // Invalidate orders list after successful payment
      queryClient.invalidateQueries({ queryKey: ["user-orders"] });
    },
  });
};

/**
 * Hook to claim a guest session after login
 */
export const useClaimGuestSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ClaimGuestSessionRequest) => claimGuestSession(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["order-session", variables.sessionId],
      });
      queryClient.invalidateQueries({ queryKey: ["order-sessions"] });
    },
  });
};

export const useAddBalance = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddBalanceRequest) => addBalance(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-balance"] });
    },
  });
};

// Apply coupon to order session
export const useApplyCoupon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ApplyCouponRequest) => applyCoupon(data),
    onSuccess: (_, variables) => {
      // Invalidate the specific order session to reflect discounted totals
      queryClient.invalidateQueries({
        queryKey: ["order-session", variables.sessionId],
      });
    },
  });
};
